/**
 * CLIENT MATCH STATE — the single object the HUD reads.
 *
 * The net subsystem owns this and mutates it in place; the HUD polls it once
 * per frame. Nothing here allocates per frame and nothing here touches the DOM:
 * `src/ui` is the only directory allowed to do that (see ARCHITECTURE.md).
 *
 * Deliberately a plain mutable struct rather than an event-emitting store — at
 * 60 fps a HUD that rebuilds on every change is a HUD that drops frames.
 */
import { TEAM } from '../net/protocol.js';

export function createMatchState() {
  return {
    /* ---- connection ---- */
    connected: false,
    connecting: false,
    latencyMs: 0,
    serverTick: 0,
    /** Client's own predicted tick, for debug display. */
    clientTick: 0,
    /** How far prediction had to be corrected last snapshot, metres. */
    correction: 0,

    /* ---- identity ---- */
    id: null,
    name: '',
    team: TEAM.NONE,

    /* ---- own vitals ---- */
    hp: 100,
    armor: 0,
    helmet: false,
    money: 800,
    alive: true,
    kills: 0,
    deaths: 0,

    /* ---- weapon ---- */
    weapon: 0,
    weaponKey: 'glock',
    weaponName: 'Glock-18',
    ammo: 0,
    magSize: 0,
    reserve: 0,
    reloading: false,
    ads: 0,

    /* ---- match ---- */
    mode: 'snd',
    map: 'de_ducks',
    score: { [TEAM.T]: 0, [TEAM.CT]: 0 },
    round: 0,
    phase: 'warmup',
    timeLeft: 0,
    matchOver: false,
    winner: TEAM.NONE,
    inBuyWindow: false,

    /* ---- objective ---- */
    hasBomb: false,
    defuseKit: false,
    bomb: {
      state: 'carried',      // carried | planted | dropped | detonated | defused
      site: null,
      x: 0, y: 0, z: 0,
      fuse: 0,
      plantProgress: 0,
      defuseProgress: 0,
      carrier: null,
    },
    /** Non-zero while this client is planting or defusing. */
    plantProgress: 0,
    defuseProgress: 0,

    /* ---- scoreboard ---- */
    players: [],   // [{ id, name, team, kills, deaths, money, alive, ping, bot, hasBomb }]

    /* ---- transient, consumed by the HUD then zeroed ---- */
    hitmarker: { t: 0, headshot: false, killed: false },
    damageFlash: { t: 0, amount: 0, fromX: 0, fromZ: 0 },
    killfeed: [],  // [{ id, killer, victim, weapon, headshot, killerTeam, victimTeam, t }]
  };
}

/** Ring of recent killfeed entries; the HUD reads it, net owns it. */
export function pushKillfeed(state, entry, max = 6) {
  state.killfeed.push(entry);
  if (state.killfeed.length > max) state.killfeed.shift();
}
